import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hostingService, HostingPackage } from '../../services/hostingService';
import { useAuth } from '../../contexts/AuthContext';
import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import DomainStep from '../../components/checkout/DomainStep';
import PlanStep from '../../components/checkout/PlanStep';
import PaymentStep from '../../components/checkout/PaymentStep';
import ConfirmationStep from '../../components/checkout/ConfirmationStep';

export interface EmailCheckoutData {
  packageId: string;
  packageName: string;
  domainName: string;
  domainAction: 'register' | 'transfer' | 'existing';
  billingCycle: 'monthly' | 'yearly';
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company?: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };
  pricing: {
    // Single source of truth for the service line (PlanStep emits it).
    servicePrice?: number;
    // Kept for compatibility with PaymentStep / backend pricingDetails.
    emailPrice: number;
    hostingPrice: number;
    domainPrice: number;
    discount: number;
    total: number;
    // Display-only: monthly×12 − price_yearly when positive.
    yearlySavings?: number;
  };
  customerType?: 'new' | 'existing' | 'unknown';
  isExistingCustomer?: boolean;
  isNewCustomerAccountCreated?: boolean;
  orderId?: string;
  orderNumber?: string;
}

const EmailCheckout: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [emailPackage, setEmailPackage] = useState<HostingPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const [checkoutData, setCheckoutData] = useState<EmailCheckoutData>({
    packageId: packageId || '',
    packageName: '',
    domainName: '',
    // Default to 'existing' so a domain charge is NOT added unless the
    // customer explicitly picks "register a new domain" on the Domain step.
    domainAction: 'existing',
    billingCycle: 'monthly',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
      }
    },
    pricing: {
      servicePrice: 0,
      emailPrice: 0,
      hostingPrice: 0,
      domainPrice: 0, // Only set to a non-zero value if customer picks 'register'
      discount: 0,
      total: 0
    }
  });

  useEffect(() => {
    if (packageId) {
      fetchPackage();
    }
  }, [packageId]);

  // Check for existing login and populate user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    const pendingCheckout = sessionStorage.getItem('pendingEmailCheckout');
    
    if (token) {
      console.log('👤 User is signed in, loading profile and skipping email verification');
      
      // Load user profile for pre-population
      if (user) {
        setCheckoutData(prev => ({
          ...prev,
          contactInfo: {
            ...prev.contactInfo,
            firstName: user.firstName || '',
            lastName: user.lastName || '', 
            email: user.email || '',
            phone: user.phone || '',
            company: user.companyName || ''
          },
          customerType: 'existing',
          isExistingCustomer: true
        }));
      }
      
      // Restore pending email checkout data if available
      if (pendingCheckout) {
        try {
          const pendingData = JSON.parse(pendingCheckout);
          
          if (pendingData.contactInfo) {
            setCheckoutData(prev => ({
              ...prev,
              contactInfo: pendingData.contactInfo,
              customerType: 'existing',
              isExistingCustomer: true
            }));
          }
          
          sessionStorage.removeItem('pendingEmailCheckout');
        } catch (error) {
          console.error('Error restoring pending Email checkout:', error);
          sessionStorage.removeItem('pendingEmailCheckout');
        }
      }
      
      // For signed-in users: Start at step 1 (which is now Plan step in their flow)
      setCurrentStep(1);
    }
  }, [user]);

  // NOTE: no pricing recalculation effect here. PlanStep is the single
  // owner of the service/total figures (it emits servicePrice + emailPrice
  // + total). A page-level recalculation used to overwrite PlanStep's
  // numbers without hostingPrice and crashed PaymentStep on `.toFixed`.

  const fetchPackage = async () => {
    // Check if user is signed in - if not, show login prompt
    if (!user && !localStorage.getItem('token')) {
      setShowLoginPrompt(true);
      setLoading(false);
      // Still fetch package for UI display
      try {
        const result = await hostingService.getEmailPackages();
        if (result.success) {
          const selectedPackage = result.data.find((pkg: HostingPackage) => pkg.id === packageId);
          if (selectedPackage) {
            setEmailPackage(selectedPackage);
            setCheckoutData(prev => ({
              ...prev,
              packageName: selectedPackage.name
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching email package for login prompt:', error);
      }
      return;
    }

    try {
      const result = await hostingService.getEmailPackages();
      if (result.success) {
        const selectedPackage = result.data.find((pkg: HostingPackage) => pkg.id === packageId);
        if (selectedPackage) {
          setEmailPackage(selectedPackage);
          setCheckoutData(prev => ({
            ...prev,
            packageName: selectedPackage.name
          }));
        } else {
          setError('Email package not found');
        }
      } else {
        setError('Failed to fetch Email package details');
      }
    } catch (error) {
      setError('Failed to fetch Email package details');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = (stepData: any) => {
    setCheckoutData(prev => ({ ...prev, ...stepData }));
    setCurrentStep(prev => prev + 1);
  };

  const handleStepBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleLoginRedirect = () => {
    const currentUrl = window.location.pathname;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/login?return=${returnUrl}&message=Please sign in to purchase email services`);
  };

  const LoginPrompt = () => (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Required</h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          To purchase business email services, you'll need a 12and22 account. This helps us manage your email setup and provide ongoing support.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleLoginRedirect}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Sign In to Continue</span>
          </button>
          
          <button
            onClick={() => navigate('/register', { 
              state: { returnUrl: window.location.pathname }
            })}
            className="border-2 border-orange-400 text-orange-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gradient-to-br hover:from-orange-400 hover:via-yellow-500 hover:to-orange-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Create Account</span>
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Why do I need an account?</h4>
          <ul className="text-sm text-blue-800 space-y-1 text-left">
            <li>• Manage your business email accounts and settings</li>
            <li>• Access webmail and email configuration tools</li>
            <li>• Receive important service notifications</li>
            <li>• Get priority customer support</li>
          </ul>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white relative z-10"></div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Loading Email Checkout</h3>
              <p className="text-gray-600">Preparing your email package...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !emailPackage) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <svg className="h-8 w-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Checkout Error</h3>
              <p className="text-gray-600 mb-6">{error || 'Email package not found'}</p>
              <button 
                onClick={() => navigate('/email')}
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Back to Email Services</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // F4 + F6: Login enforced upstream. Single canonical step sequence.
  const steps = [
    { number: 1, title: 'Domain', description: 'Register or use existing' },
    { number: 2, title: 'Plan', description: 'Billing preferences' },
    { number: 3, title: 'Payment', description: 'Secure payment' },
    { number: 4, title: 'Confirmation', description: 'Email setup' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
            <span className="text-sm font-medium text-gray-700">Business Email</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-4">
            Complete Your Order
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
              {emailPackage.name}
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            You're almost done! Let's get your professional email set up
          </p>
        </div>

        {/* Progress Steps */}
        <CheckoutSteps steps={steps} currentStep={currentStep} />

        {/* Step Content */}
        <div className="mt-8">
          {showLoginPrompt ? (
            <LoginPrompt />
          ) : currentStep === 1 ? (
            // DomainStep is typed against HostingCheckout's CheckoutData,
            // but the only fields it reads/writes (domainName, domainAction)
            // exist on EmailCheckoutData with identical shape — safe to cast.
            <DomainStep
              data={checkoutData as any}
              onNext={handleStepComplete as any}
              onBack={() => navigate('/email')}
              product="email"
            />
          ) : currentStep === 2 ? (
            <PlanStep
              data={checkoutData}
              package={emailPackage}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 3 ? (
            <PaymentStep
              data={checkoutData}
              package={emailPackage}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 4 ? (
            <ConfirmationStep
              data={checkoutData}
              package={emailPackage}
              serviceType="Email"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EmailCheckout;